import { Component, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ReactiveFormsModule, FormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { CaseService } from '../../../core/services/case.service';
import { AuthService } from '../../../core/services/auth.service';
import { AmountFormatDirective } from '../../../shared/directives/amount-format.directive';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { PencilEdit01Icon, Pdf02Icon, File01Icon, ViewIcon, Upload01Icon, EyeIcon, ViewOffIcon, Copy01Icon, Invoice04Icon, CheckmarkCircle02Icon, Delete02Icon } from '@hugeicons/core-free-icons';
import { CaseOfferComponent, OfferDetail } from './case-offer/case-offer.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { FileUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { CaseRemindersComponent, Reminder, ReminderAction } from './case-reminders/case-reminders.component';
import { DocumentsPreviewModalComponent } from './documents-preview-modal/documents-preview-modal.component';
import { DocCoverLetterComponent } from './documents-preview-modal/doc-cover-letter/doc-cover-letter.component';
import { DocAcknowledgmentComponent } from './documents-preview-modal/doc-acknowledgment/doc-acknowledgment.component';
import { DocAssignmentTrustFundsComponent } from './documents-preview-modal/doc-assignment-trust-funds/doc-assignment-trust-funds.component';
import { DocHipaaAuthorizationComponent } from './documents-preview-modal/doc-hipaa-authorization/doc-hipaa-authorization.component';
import { DocReleasOfLienComponent } from './documents-preview-modal/doc-release-of-lien/doc-release-of-lien.component';
import { DocumentService } from '../../../core/services/document.service';
import { DocumentCaseData } from '../../../core/services/document.models';
import { DocSignState, blankSigner } from './documents-preview-modal/doc-models';
import { PDFDocument } from 'pdf-lib';
import html2canvas from 'html2canvas';
import { CaseDetail, CaseDocument, ProgressStep, PaymentDetail } from './case-details.models';
import { CaseDocumentCenterComponent } from './case-document-center/case-document-center.component';
import { PhoneFormatDirective } from '../../../shared/directives/phone-format.directive';

@Component({
    selector: 'app-case-details',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, AmountFormatDirective, PhoneFormatDirective, HugeiconsIconComponent, CaseOfferComponent, ModalComponent, DatePickerComponent, FileUploadComponent, CaseRemindersComponent, DocumentsPreviewModalComponent, DocCoverLetterComponent, DocAcknowledgmentComponent, DocAssignmentTrustFundsComponent, DocHipaaAuthorizationComponent, DocReleasOfLienComponent, CaseDocumentCenterComponent],
    templateUrl: './case-details.component.html',
    styleUrl: './case-details.component.scss'
})
export class CaseDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
    caseId: string | null = null;
    inquiryId: number | null = null;
    offerStatus: string | null = null;
    caseData: CaseDetail | null = null;
    offerData: OfferDetail | null = null;
    offerId: number | null = null;
    showOfferModal = false;
    showViewOfferModal = false;
    showSubmittedDocsModal = false;

    // Document Preview & Sign modal (replaces upload doc + e-sign modals)
    showDocumentsModal = false;
    /** Which doc tab to open (0=Acknowledgment, 1=ATF, 2=HIPAA). Used when Sign is clicked on a specific doc. */
    docSignIndex = 0;
    /**
     * Controls which mode the Documents modal opens in.
     * 'select' = show mode-selection screen (default, e.g. from reminder banners).
     * 'esign'  = jump straight to the e-sign canvas for docSignIndex (from Sign button in offer modal).
     */
    docStartMode: 'select' | 'esign' = 'select';
    /** Frontend doc indices that were rejected by admin — passed to the modal to filter listings. */
    docRejectedIndices: number[] = [];

    // Progress overlay (PDF generation)
    progressPercent: number | null = null;
    progressLabel = '';

    // Cover Letter direct download
    showCoverLetterRender = false;
    coverLetterData: DocumentCaseData | null = null;
    coverLetterDownloading = false;
    private caseDateOfService: string | null = null;

    @ViewChild('coverLetterDownloadComp') coverLetterDownloadComp?: DocCoverLetterComponent;
    @ViewChild('stickySentinel') stickySentinel?: ElementRef;
    @ViewChild('stickyWrapper') stickyWrapper?: ElementRef;
    private stickyObserver?: IntersectionObserver;

    // Document PDF download/view (without signing)
    showDocPdfRender = false;
    docPdfIndex: 0 | 1 | 2 = 0;
    docPdfData: DocumentCaseData | null = null;
    downloadingDocIndex: number | null = null;
    viewingDocIndex: number | null = null;
    coverLetterViewing = false;

    @ViewChild('ackDownloadComp')  ackDownloadComp?:  DocAcknowledgmentComponent;
    @ViewChild('atfDownloadComp')  atfDownloadComp?:  DocAssignmentTrustFundsComponent;
    @ViewChild('hipaaDownloadComp') hipaaDownloadComp?: DocHipaaAuthorizationComponent;
    @ViewChild('rolDownloadComp') rolDownloadComp?: DocReleasOfLienComponent;

    // Release of Lien PDF generation
    showRolPdfRender = false;
    rolPdfData: DocumentCaseData | null = null;
    generatingRol = false;

    get blankDocState(): DocSignState {
        return { ...blankSigner(), attorney: blankSigner() };
    }

    private settlementStatusLabels: Record<string, string> = {
        PRE_SUIT: 'In Pre-Suit Negotiations',
        LITIGATION: 'In Litigation',
        SETTLED: 'Settled'
    };

    getSettlementStatusLabel(value: string | undefined): string {
        return value ? (this.settlementStatusLabels[value] || value) : '';
    }

    // Send Payment modal
    showSendPaymentModal = false;
    sendPaymentForm: FormGroup;
    sendPaymentSubmitting = false;

    // Review Signed Documents modal (admin)
    showReviewDocsModal = false;
    docActionSubmitting: Record<number, boolean> = {};

    showPaymentModal = false;
    showPaymentDetailsModal = false;
    showReceivedPaymentModal = false;
    showReceiptModal = false;
    showReleaseOfLienModal = false;
    releaseOfLienForm: FormGroup;
    releaseOfLienSubmitting = false;
    showReceivedMoneyConfirmModal = false;
    receivedMoneyForm: FormGroup;
    receivedMoneySubmitting = false;
    showCelebration = false;

    // Hospital Payment modal
    showHospitalPaymentModal = false;
    showViewHospitalPaymentModal = false;
    showUpdateHospitalPaymentModal = false;
    hospitalPaymentData: any = null;
    hospitalPaymentForm: FormGroup;
    updateHospitalPaymentForm: FormGroup;
    hospitalPaymentSubmitting = false;
    updateHospitalPaymentSubmitting = false;

    offerForm: FormGroup;
    paymentForm: FormGroup;
    progressSteps: ProgressStep[] = [];
    paymentSubmitting = false;
    paymentDetails: PaymentDetail | null = null;
    showPaymentField: Record<string, boolean> = {};
    offerDataLoaded = false;
    isPageLoading = true;

    // Icons
    editIcon = PencilEdit01Icon;
    pdfIcon = Pdf02Icon;
    fileIcon = File01Icon;
    viewIcon = ViewIcon;
    invoiceIcon = Invoice04Icon;
    uploadIcon = Upload01Icon;
    eyeIcon = EyeIcon;
    eyeOffIcon = ViewOffIcon;
    copyIcon = Copy01Icon;
    checkCircleIcon = CheckmarkCircle02Icon;
    deleteIcon = Delete02Icon;

    constructor(
        private route: ActivatedRoute,
        private snackbar: SnackbarService,
        private caseService: CaseService,
        private documentService: DocumentService,
        private fb: FormBuilder,
        private authService: AuthService,
        private cdr: ChangeDetectorRef
    ) {
        this.offerForm = this.fb.group({
            offer_amount: ['', [Validators.required, Validators.min(0)]],
            confirm_offer_amount: ['', [Validators.required]],
            instructions: ['']
        }, {
            validators: this.offerAmountMatchValidator
        });

        this.paymentForm = this.fb.group({
            full_name: ['', Validators.required],
            mailing_street: ['', Validators.required],
            mailing_city: ['', Validators.required],
            mailing_state: ['', Validators.required],
            mailing_zip: ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
            address_type: ['', Validators.required],
            phone_number: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
            email_address: ['']
        });

        this.sendPaymentForm = this.fb.group({
            transaction_id: ['', Validators.required],
            transaction_date: ['', Validators.required],
            amount: ['', [Validators.required, Validators.min(0)]],
            receipt: [null, Validators.required]
        });

        this.releaseOfLienForm = this.fb.group({
            document: [null, Validators.required]
        });

        this.hospitalPaymentForm = this.fb.group({
            hospital_name: ['', Validators.required],
            address: ['', Validators.required],
            phone_number: ['', Validators.required],
            street: ['', Validators.required],
            city: ['', Validators.required],
            state: ['', Validators.required],
            zipcode: ['', Validators.required],
            amount: ['', [Validators.required, Validators.min(0)]],
            attachement: [null, Validators.required]
        });

        this.updateHospitalPaymentForm = this.fb.group({
            transaction_id: ['', Validators.required],
            transaction_date: ['', Validators.required],
            amount: ['', [Validators.required, Validators.min(0)]],
            receipt: [null, Validators.required]
        });

        this.receivedMoneyForm = this.fb.group({
            amount: ['', [Validators.required, Validators.min(0)]],
            cheque_id: ['', Validators.required],
            cheque_date: ['', Validators.required],
            attachement: [null, Validators.required]
        });
    }

    get isAttorney(): boolean {
        return this.authService.getRole() === 'ATTORNEY';
    }

    get isAssistant(): boolean {
        return this.authService.getRole() === 'ASSISTANT';
    }

    private readonly EXCLUDED_DOCUMENT_TYPES = ['Cover Letter', 'Release of Lien'];

    /** Maps API document_type strings to frontend doc indices used by the Documents modal */
    private readonly DOC_TYPE_TO_INDEX: Record<string, number> = {
        'Acknowledgment of Assignment': 0,
        'Assignment of Trust Funds': 1,
        'Hipaa Authorization': 2,
    };

    /** The 3 documents that must all be signed AND admin-accepted before the attorney sees the Payment Details banner */
    private readonly REQUIRED_SIGNED_DOCUMENTS = [
        'Assignment of Trust Funds',
        'Acknowledgment of Assignment',
        'Hipaa Authorization'
    ];

    /** True when at least one required document has NOT been signed by the attorney yet */
    get hasUnsignedDocuments(): boolean {
        if (!this.offerData || !this.offerData.documents?.length) return true;
        const docs = this.offerData.documents;
        return this.REQUIRED_SIGNED_DOCUMENTS.some(requiredType =>
            docs.some((doc: any) => doc.document_type === requiredType && !doc.file_name_signed)
        );
    }

    get allDocumentsSigned(): boolean {
        // offerData must be loaded and have documents — guard against empty/null
        if (!this.offerData || !this.offerData.documents?.length) return false;
        const docs = this.offerData.documents;
        // All 3 required document types must exist, be signed (file_name_signed),
        // AND have accept_status accepted by admin
        return this.REQUIRED_SIGNED_DOCUMENTS.every(requiredType =>
            docs.some((doc: any) =>
                doc.document_type === requiredType &&
                !!doc.file_name_signed &&
                this.normaliseAcceptStatus(doc) === 'accepted'
            )
        );
    }

    /** True when all required docs have been e-signed by attorney and none are rejected */
    get allDocumentsEsigned(): boolean {
        if (!this.offerData || !this.offerData.documents?.length) return false;
        const docs = this.offerData.documents;
        return this.REQUIRED_SIGNED_DOCUMENTS.every(requiredType =>
            docs.some((doc: any) =>
                doc.document_type === requiredType &&
                !!doc.file_name_signed &&
                !this.isDocRejected(doc)
            )
        );
    }

    get reminders(): Reminder[] {
        const list: Reminder[] = [];

        // Attorney: case submitted, waiting for offer to be generated
        if (this.isAttorney && !this.offerStatus && this.caseData?.caseProgress && this.caseData.caseProgress < 2) {
            list.push({
                title: 'Waiting for Offer',
                text: 'Your case has been submitted successfully. Please wait while the admin reviews and generates an offer.'
            });
        }

        if (this.offerStatus === 'PENDING') {
            if (this.isAttorney) {
                list.push({
                    title: 'Offer Received',
                    text: 'A new offer has been generated for this case. Please review and take action.',
                    action: { label: 'View Offer', icon: this.viewIcon, btnClass: 'btn btn-sm btn-default', handler: () => this.openViewOfferModal() }
                });
            } else {
                list.push({
                    title: 'Offer Generated',
                    text: 'You have successfully generated an offer for this case. Waiting for the attorney to review and respond.',
                    action: { label: 'View Offer', icon: this.viewIcon, btnClass: 'btn btn-sm btn-default', handler: () => this.openViewOfferModal() }
                });
            }
        }

        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && !this.hasSignedDocsPendingReview && !this.allDocumentsSigned && !this.hasRejectedDocs) {
            const pendingNames = this.unsignedDocNames;
            const pendingText = pendingNames.length
                ? ` (${pendingNames.join(', ')})`
                : '';
            list.push({
                title: 'Waiting for Signed Documents',
                text: `The offer has been accepted by the attorney. Waiting for them to upload the required signed documents${pendingText}.`
            });
        }

        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && this.hasRejectedDocs) {
            list.push({
                title: 'Document(s) Rejected',
                text: `You have rejected the following document(s): ${this.rejectedDocNames.join(', ')}. Waiting for the attorney to re-upload them.`,
                variant: 'error'
            });
        }

        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && this.hasSignedDocsPendingReview) {
            list.push({
                title: 'Signed Documents Pending Review',
                text: 'The attorney has submitted signed documents. Please review and approve or reject them.',
                action: { label: 'View Signed', icon: this.viewIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openReviewDocsModal() }
            });
        }

        if (this.isAttorney && this.offerStatus === 'ACCEPTED' && this.hasRejectedDocs && !this.allDocumentsSigned) {
            list.push({
                title: 'Document(s) Rejected — Action Required',
                text: `The following document(s) were rejected by admin: ${this.rejectedDocNames.join(', ')}. Please re-sign or re-upload them.`,
                variant: 'error',
                action: { label: 'Re-upload Documents', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => { this.docRejectedIndices = this.rejectedDocFrontendIndices; this.showDocumentsModal = true; } }
            });
        }

        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && this.allDocumentsSigned && this.paymentDetails?.status !== 'SENT') {
            const actions: ReminderAction[] = [];
            if (this.paymentDetails) {
                actions.push({ label: 'Payment Details', icon: this.viewIcon, btnClass: 'btn btn-md btn-default', handler: () => this.openPaymentDetailsModal() });
            }
            actions.push({ label: 'Send Patient Payment', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openSendPaymentModal() });
            list.push({
                title: 'Send Patient Payment',
                text: 'All documents have been accepted. Please review and send the patient payment to proceed.',
                actions
            });
        }

        if (this.isAttorney && this.offerStatus === 'ACCEPTED' && this.hasUnsignedDocuments && !this.allDocumentsSigned && !this.hasRejectedDocs) {
            const pendingNames = this.unsignedDocNames;
            const pendingText = pendingNames.length
                ? ` (${pendingNames.join(', ')})`
                : '';
            const uploadActions: ReminderAction[] = [
                { label: 'Upload Document', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => { this.docRejectedIndices = this.pendingDocFrontendIndices; this.showDocumentsModal = true; } }
            ];
            if (this.submittedSignedDocuments.length > 0) {
                uploadActions.unshift({ label: 'View Submitted Documents', icon: this.viewIcon, btnClass: 'btn btn-md btn-default', handler: () => this.openSubmittedDocsModal() });
            }
            list.push({
                title: 'Upload Pending Documents',
                text: `The offer has been accepted. Please upload the required signed documents${pendingText}.`,
                actions: uploadActions
            });
        }

        if (this.isAttorney && this.offerStatus === 'ACCEPTED' && !this.hasUnsignedDocuments && !this.allDocumentsSigned && !this.hasRejectedDocs) {
            list.push({
                title: 'Documents Under Revision',
                text: 'Your signed documents have been submitted and are currently under review by the admin.',
                action: this.submittedSignedDocuments.length > 0
                    ? { label: 'View Submitted Documents', icon: this.viewIcon, btnClass: 'btn btn-md btn-default', handler: () => this.openSubmittedDocsModal() }
                    : undefined
            });
        }

        if (this.isAttorney && this.offerStatus === 'ACCEPTED' && this.allDocumentsSigned && this.paymentDetails?.status !== 'SENT') {
            list.push({
                title: 'Waiting for Payment',
                text: 'All documents have been accepted. Please wait while the admin processes your payment.'
            });
        }

        // Hospital Payment — admin/assistant only (attorney skips this step)
        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && this.paymentDetails?.status === 'SENT' && this.caseData?.hospitalPayment === null) {
            if (this.isAssistant) {
                list.push({
                    title: 'Provider Negotiation',
                    text: 'Patient payment has been sent. Please submit the provider negotiation details to proceed.',
                    action: { label: 'Enter Provider Negotiated Settlement', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openHospitalPaymentModal() }
                });
            } else {
                list.push({
                    title: 'Waiting on Provider Negotiations',
                    text: 'Patient payment has been sent. Waiting for provider negotiation details to be submitted.'
                });
            }
        }

        // Hospital Payment Submitted but not paid — admin updates payment details, assistant waits
        if (!this.isAttorney && this.offerStatus === 'ACCEPTED' && this.paymentDetails?.status === 'SENT' && this.caseData?.hospitalPayment === 0) {
            if (this.isAssistant) {
                list.push({
                    title: 'Hospital Payment Pending',
                    text: 'Hospital payment details have been submitted. Waiting for hospital payment clearance.'
                });
            } else {
                list.push({
                    title: 'Update Hospital Payment',
                    text: 'Hospital payment details have been submitted. Please update the payment information (check details) to proceed.',
                    action: { label: 'Update Payment Details', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openUpdateHospitalPaymentModal() }
                });
            }
        }

        // Release of Lien — Admin/Assistant: show after hospital payment confirmed (1); Attorney: show after patient payment sent
        const hospitalPaymentDone = this.caseData?.hospitalPayment === 1;
        const showReleaseLien = this.offerStatus === 'ACCEPTED' && !this.releaseOfLienDoc && this.caseData?.caseProgress !== 7;

        if (!this.isAttorney && showReleaseLien && this.paymentDetails?.status === 'SENT' && hospitalPaymentDone) {
            list.push({
                title: 'Upload Release of Lien',
                text: 'Hospital payment has been submitted. Please upload the Release of Lien document to complete the case settlement.',
                actions: [
                    { label: 'Generate Release of Lien', icon: this.pdfIcon, btnClass: 'btn btn-md btn-default', handler: () => this.generateReleaseOfLien() },
                    { label: 'Upload Document', icon: this.uploadIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openReleaseOfLienModal() }
                ]
            });
        }

        if (this.isAttorney && showReleaseLien && this.paymentDetails?.status === 'SENT') {
            list.push({
                title: 'Patient Payment Sent',
                text: 'Your payment has been processed. Waiting for Release of Lien to be uploaded.',
                action: { label: 'View Receipt', icon: this.viewIcon, btnClass: 'btn btn-sm btn-default', handler: () => this.openReceivedPaymentModal() }
            });
        }

        // Pending Trust Funds — after release of lien uploaded
        if (this.offerStatus === 'ACCEPTED' && this.releaseOfLienDoc && !this.caseData?.moneyReceived && this.caseData?.caseProgress !== 7) {
            if (this.isAttorney) {
                list.push({
                    title: 'Waiting on Trust Funds',
                    text: 'The Release of Lien has been uploaded. Waiting for confirmation that trust funds have been received.',
                    action: { label: 'View Release of Lien', icon: this.viewIcon, btnClass: 'btn btn-sm btn-default', href: this.releaseOfLienUrl }
                });
            } else if (this.isAssistant) {
                list.push({
                    title: 'Waiting on Trust Funds',
                    text: 'The Release of Lien has been uploaded. Waiting for admin to confirm that trust funds have been received.'
                });
            } else {
                list.push({
                    title: 'Waiting on Trust Funds',
                    text: 'The Release of Lien has been uploaded. Confirm that the trust funds have been received to close this case.',
                    action: { label: 'Confirm Received Money', icon: this.checkCircleIcon, btnClass: 'btn btn-md btn-primary', handler: () => this.openReceivedMoneyConfirmModal() }
                });
            }
        }

        return list;
    }

    get hasVisibleReminders(): boolean {
        if (!this.offerDataLoaded) return false;
        if (this.reminders.length > 0) return true;
        if (this.caseData?.caseProgress === 7 || this.caseData?.moneyReceived) return true;
        if (this.offerStatus === 'REJECTED') return true;
        return false;
    }

    /** Admin: all signed documents shown in the review modal (signed, non-excluded) */
    get signedDocumentsPendingReview(): any[] {
        if (!this.offerData?.documents) return [];
        return this.offerData.documents.filter((doc: any) =>
            !!doc.file_name_signed &&
            !this.EXCLUDED_DOCUMENT_TYPES.includes(doc.document_type)
        );
    }

    /** Admin: true only when there are signed docs NOT yet actioned (pending review) */
    private get hasUnreviewedSignedDocs(): boolean {
        if (!this.offerData?.documents) return false;
        return this.offerData.documents.some((doc: any) =>
            !!doc.file_name_signed &&
            !this.EXCLUDED_DOCUMENT_TYPES.includes(doc.document_type) &&
            this.normaliseAcceptStatus(doc) === ''
        );
    }

    /** Normalises accept_status to a lowercase string regardless of whether the API returns a number (1/2) or string */
    private normaliseAcceptStatus(doc: any): string {
        const s = doc.accept_status;
        if (s === null || s === undefined) return '';
        if (s === 1 || s === '1') return 'accepted';
        if (s === 2 || s === '2') return 'rejected';
        return String(s).toLowerCase();
    }

    /** Helper: returns true if a doc has been actioned (accepted or rejected) */
    isDocActioned(doc: any): boolean {
        const s = this.normaliseAcceptStatus(doc);
        return s === 'accepted' || s === 'rejected';
    }

    /** Helper: returns true if the doc was accepted */
    isDocAccepted(doc: any): boolean {
        return this.normaliseAcceptStatus(doc) === 'accepted';
    }

    /** Helper: returns true if the doc was rejected */
    isDocRejected(doc: any): boolean {
        return this.normaliseAcceptStatus(doc) === 'rejected';
    }

    /** Admin: true when there are signed docs waiting for review (not yet accepted or rejected) */
    get hasSignedDocsPendingReview(): boolean {
        return this.hasUnreviewedSignedDocs;
    }

    /** True when admin has rejected at least one signed document */
    get hasRejectedDocs(): boolean {
        if (!this.offerData?.documents) return false;
        return this.offerData.documents.some((doc: any) =>
            !this.EXCLUDED_DOCUMENT_TYPES.includes(doc.document_type) &&
            this.isDocRejected(doc)
        );
    }

    /** Returns list of unsigned (pending) document names */
    get unsignedDocNames(): string[] {
        if (!this.offerData?.documents) return [];
        const docs = this.offerData.documents;
        return this.REQUIRED_SIGNED_DOCUMENTS.filter(requiredType =>
            docs.some((doc: any) => doc.document_type === requiredType && !doc.file_name_signed)
        );
    }

    /** Returns list of rejected document names */
    get rejectedDocNames(): string[] {
        if (!this.offerData?.documents) return [];
        return this.offerData.documents
            .filter((doc: any) =>
                !this.EXCLUDED_DOCUMENT_TYPES.includes(doc.document_type) &&
                this.isDocRejected(doc)
            )
            .map((doc: any) => doc.document_type);
    }

    /** Returns signed documents submitted by the attorney (for the submitted docs modal) */
    get submittedSignedDocuments(): any[] {
        if (!this.offerData?.documents) return [];
        return this.offerData.documents.filter((doc: any) =>
            !!doc.file_name_signed &&
            !this.EXCLUDED_DOCUMENT_TYPES.includes(doc.document_type)
        );
    }

    openSubmittedDocsModal() {
        this.showSubmittedDocsModal = true;
    }

    closeSubmittedDocsModal() {
        this.showSubmittedDocsModal = false;
    }

    viewSignedDocInNewTab(doc: any) {
        const url = this.getSignedDocUrl(doc);
        if (url) {
            window.open(url, '_blank');
        }
    }

    /** Returns frontend doc indices (0/1/2) for rejected documents */
    get rejectedDocFrontendIndices(): number[] {
        return this.rejectedDocNames
            .map(name => this.DOC_TYPE_TO_INDEX[name])
            .filter((i): i is number => i !== undefined);
    }

    /** Returns frontend doc indices (0/1/2) for documents that are NOT yet accepted */
    get pendingDocFrontendIndices(): number[] {
        if (!this.offerData?.documents) return [0, 1, 2];
        const docs = this.offerData.documents;
        return this.REQUIRED_SIGNED_DOCUMENTS
            .filter(requiredType =>
                !docs.some((doc: any) =>
                    doc.document_type === requiredType &&
                    !!doc.file_name_signed &&
                    this.normaliseAcceptStatus(doc) === 'accepted'
                )
            )
            .map(name => this.DOC_TYPE_TO_INDEX[name])
            .filter((i): i is number => i !== undefined);
    }

    /** True when a Release of Lien document has been uploaded (file_name_unsigned set) */
    get releaseOfLienDoc(): any | null {
        if (!this.offerData?.documents) return null;
        return this.offerData.documents.find((d: any) =>
            d.document_type === 'Release of Lien' && !!d.file_name_unsigned
        ) || null;
    }

    get releaseOfLienUrl(): string {
        const doc = this.releaseOfLienDoc;
        if (!doc || !this.offerData?.default_url) return '';
        return `${this.offerData.default_url}/${doc.file_path}/${doc.file_name_unsigned}`;
    }

    openReviewDocsModal(): void {
        this.showReviewDocsModal = true;
    }

    closeReviewDocsModal(): void {
        this.showReviewDocsModal = false;
    }

    approveDocument(doc: any): void {
        if (this.docActionSubmitting[doc.id]) return;
        this.docActionSubmitting[doc.id] = true;
        this.caseService.acceptDocument(doc.id, 1).subscribe({
            next: () => {
                doc.accept_status = 'accepted';
                this.docActionSubmitting[doc.id] = false;
                this.snackbar.show('Document accepted', 'success');
                this.closeReviewModalIfAllActioned();
            },
            error: () => {
                this.docActionSubmitting[doc.id] = false;
                this.snackbar.show('Failed to accept document', 'error');
            }
        });
    }

    rejectDocument(doc: any): void {
        if (this.docActionSubmitting[doc.id]) return;
        this.docActionSubmitting[doc.id] = true;
        this.caseService.acceptDocument(doc.id, 2).subscribe({
            next: () => {
                doc.accept_status = 'rejected';
                this.docActionSubmitting[doc.id] = false;
                this.snackbar.show('Document rejected', 'success');
                this.closeReviewModalIfAllActioned();
            },
            error: () => {
                this.docActionSubmitting[doc.id] = false;
                this.snackbar.show('Failed to reject document', 'error');
            }
        });
    }

    /** Close the review modal automatically once every signed doc has been accepted or rejected */
    private closeReviewModalIfAllActioned(): void {
        const allActioned = this.signedDocumentsPendingReview.every(doc => this.isDocActioned(doc));
        if (allActioned) {
            this.showReviewDocsModal = false;
        }
    }

    getSignedDocUrl(doc: any): string {
        if (!this.offerData?.default_url || !doc.file_name_signed) return '';
        return `${this.offerData.default_url}/${doc.file_path}/${doc.file_name_signed}`;
    }

    private mapOfferResponse(response: any): void {
        if (!response?.data) return;
        this.offerId = response.data.id;
        const allDocs = response.documents || [];
        const coverLetterDoc = allDocs.find((d: any) => d.document_type === 'Cover Letter');
        const coverLetterPath = coverLetterDoc
            ? `${coverLetterDoc.file_path}/${coverLetterDoc.file_name_unsigned}`
            : (response.data.cover_letter || response.cover_letter || '');
        this.offerData = {
            offer_amount: response.data.offer_amount,
            offer_status: response.data.offer_status,
            instructions: response.data.instructions,
            offer_issued_at: response.data.offer_issued_at,
            documents: allDocs,
            default_url: response.default_url || '',
            cover_letter: coverLetterPath
        };
    }

    ngOnInit(): void {
        this.caseId = this.route.snapshot.paramMap.get('id');
        this.loadCaseData();

        this.route.queryParams.subscribe(params => {
            if (params['openPayment'] === 'true') {
                this.showPaymentModal = true;
            }
        });
    }

    ngAfterViewInit(): void {
        this.setupStickyObserver();
    }

    ngOnDestroy(): void {
        this.stickyObserver?.disconnect();
    }

    private setupStickyObserver(): void {
        // Re-check after data loads since the element may not exist yet
        setTimeout(() => this.observeSentinel(), 600);
    }

    private observeSentinel(): void {
        const sentinel = this.stickySentinel?.nativeElement;
        const wrapper = this.stickyWrapper?.nativeElement;
        if (!sentinel || !wrapper) return;

        this.stickyObserver?.disconnect();
        this.stickyObserver = new IntersectionObserver(
            ([entry]) => {
                // When sentinel scrolls out of view, the wrapper is stuck
                if (entry.isIntersecting) {
                    wrapper.classList.remove('is-stuck');
                } else {
                    wrapper.classList.add('is-stuck');
                }
            },
            { threshold: 0 }
        );
        this.stickyObserver.observe(sentinel);
    }

    loadCaseData() {
        if (!this.caseId) return;
        this.isPageLoading = true;
        this.offerDataLoaded = false;

        this.caseService.getCaseById(this.caseId).subscribe({
            next: (data) => {
                this.inquiryId = data.case_id;
                this.offerStatus = data.offer_status;
                this.caseDateOfService = data.date_of_admission || data.date_of_service || data.lien?.date_of_service || null;
                this.caseData = {
                    id: data.case_code || `CAS-${data.id}`,
                    title: data.case_title,
                    clientName: data.client?.full_name || 'N/A',
                    clientEmail: data.client?.email || 'N/A',
                    clientPhone: data.client?.phone || 'N/A',
                    clientDob: data.client?.dob,
                    clientAddress: data.client?.patient_address || 'N/A',
                    postingAddress: data.client?.posting_address || 'N/A',
                    attorneyName: data.attorney?.name || 'N/A',
                    attorneyFirm: data.attorney?.firm || 'N/A',
                    attorneyEmail: data.attorney?.email || 'N/A',
                    attorneyPhone: data.attorney?.phone || 'N/A',
                    caseType: data.type,
                    hospitalName: data.lien?.hospital_provider || 'N/A',
                    hospitalAccountNumber: data.lien?.hospital_account_number || 'N/A',
                    lienBalance: data.lien?.current_balance || parseFloat(data.financials?.lien_amount || '0'),
                    reductionAmount: data.lien?.reduction_amount ?? null,
                    status: data.status,
                    caseProgress: parseInt(data.case_progress, 10) || 1,
                    progress: data.progress?.percent || 0,
                    progressHint: data.progress?.hint,
                    prelimLienSummary: data.preliminary_lien_summary || 'No summary provided',
                    supportingDetails: data.lien?.treatment_details || data.lien?.supporting_details || 'No details provided',
                    reductionsRequested: data.lien?.reductions_requested || false,
                    date: new Date().toISOString(),
                    defaultUrl: data.default_url || '',
                    financials: data.financials ? {
                        lienAmount: parseFloat(data.financials.lien_amount || '0'),
                        settlementStatus: data.financials.settlement_status,
                        settlementAmount: parseFloat(data.financials.settlement_amount || '0'),
                        settlementLoans: data.financials.settlement_loans,
                        loanAmount: parseFloat(data.financials.loan_amount || '0'),
                        totalMedicals: parseFloat(data.financials.total_medicals || '0'),
                        legalFees: parseFloat(data.financials.legal_fees || '0'),
                        costs: parseFloat(data.financials.costs || '0')
                    } : undefined,
                    paymentStatus: data.payment?.status || null,
                    releaseSent: data.release_sent || false,
                    moneyReceived: data.money_received || false,
                    hospitalPayment: data.hospital_payment != null ? Number(data.hospital_payment) : null,
                    documents: (data.documents || [])
                        .filter((doc: any) => !doc.file_variable)
                        .map((doc: any) => ({
                            id: doc.id,
                            name: doc.name || doc.file_name_unsigned,
                            type: doc.type || '',
                            uploadedAt: doc.uploaded_at,
                            sizeMb: doc.size_mb || 0,
                            file_path: doc.storage_path || doc.file_path
                        })),
                    submissionDocs: this.mapSubmissionDocs(data)
                };
                this.computeProgressSteps();

                // Store hospital payment details for view modal
                this.hospitalPaymentData = data.hospital_payment_detail || data.hospital_payment_details || null;

                // Auto-load offer data when an offer exists
                if (this.offerStatus && this.inquiryId) {
                    this.loadOfferData();
                } else {
                    this.offerDataLoaded = true;
                    this.isPageLoading = false;
                }

                // Map payment details from case detail response
                if (data.payment) {
                    this.paymentDetails = this.mapPaymentResponse(data.payment);
                }

            },
            error: () => {
                this.snackbar.show('Error loading case details', 'error');
                this.isPageLoading = false;
            }
        });
    }

    private mapSubmissionDocs(data: any) {
        const allDocs = data.documents || [];
        const filterByFileVariable = (fileVariable: string): CaseDocument[] =>
            allDocs
                .filter((doc: any) => doc.file_variable === fileVariable)
                .map((f: any) => ({
                    id: f.id,
                    name: f.name || f.file_name_unsigned || f.file_name,
                    type: f.type || '',
                    uploadedAt: f.uploaded_at,
                    sizeMb: f.size_mb || 0,
                    file_path: f.storage_path || f.file_path
                }));
        return {
            partialClosingStatement: {
                status: data.partial_closing_statement_status != null ? Number(data.partial_closing_statement_status) : null,
                reason: data.partial_closing_statement_reason || '',
                files: filterByFileVariable('partial_closing_statement')
            },
            billOrInvoice: {
                status: data.bill_or_invoice_status != null ? Number(data.bill_or_invoice_status) : null,
                reason: data.bill_or_invoice_reason || '',
                files: filterByFileVariable('bill_or_invoice')
            },
            hospitalClaimLien: {
                status: data.hosipital_claim_lien_status != null ? Number(data.hosipital_claim_lien_status) : null,
                reason: data.hosipital_claim_lien_reason || '',
                files: filterByFileVariable('hosipital_claim_lien')
            },
            correspondenceLienholder: {
                status: data.correspondence_lienholder_status != null ? Number(data.correspondence_lienholder_status) : null,
                reason: data.correspondence_lienholder_reason || '',
                files: filterByFileVariable('correspondence_lienholder')
            }
        };
    }

    loadPaymentDetails() {
        if (!this.caseId) return;

        this.caseService.getPaymentDetails(this.caseId).subscribe({
            next: (data) => {
                this.paymentDetails = this.mapPaymentResponse(data);
            },
            error: () => { }
        });
    }

    private mapPaymentResponse(data: any): PaymentDetail {
        return {
            id: data.id,
            case_id: data.case_id,
            case_offer_id: data.case_offer_id,
            full_name: data.full_name ?? '',
            mailing_street: data.mailing_street ?? data.street ?? '',
            mailing_city: data.mailing_city ?? data.city ?? '',
            mailing_state: data.mailing_state ?? data.state ?? '',
            mailing_zip: data.mailing_zip ?? data.zipcode ?? '',
            address_type: data.address_type ?? (data.business_or_residence === 1 ? 'BUSINESS' : data.business_or_residence === 2 ? 'RESIDENCE' : ''),
            phone_number: data.phone_number ?? '',
            email_address: data.email_address ?? data.email ?? '',
            status: data.status,
            transaction_id: data.transaction_id,
            transaction_date: data.transaction_date,
            amount: data.amount,
            receipt: data.receipt,
            receipt_path: data.receipt_path,
            created_at: data.created_at
        };
    }

    getSubmissionDocUrl(docGroup: any): string {
        if (docGroup.files?.length && docGroup.files[0].file_path) {
            return `${this.caseData?.defaultUrl}/${docGroup.files[0].file_path}`;
        }
        return '';
    }

    formatPhone(phone: string): string {
        if (!phone) return '';
        const digits = phone.replace(/\D/g, '');
        if (digits.length === 10) {
            return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
        }
        return phone;
    }

    loadOfferData(silent = false) {
        if (!this.inquiryId) return;

        this.caseService.getOfferByCaseId(this.inquiryId, silent).subscribe({
            next: (response) => {
                this.mapOfferResponse(response);
                if (response?.data) {
                    this.computeProgressSteps();
                }
                this.offerDataLoaded = true;
                this.isPageLoading = false;
            },
            error: () => {
                this.offerDataLoaded = true;
                this.isPageLoading = false;
            }
        });
    }

    computeProgressSteps(): void {
        if (!this.caseData) return;

        const isRejected = this.offerStatus === 'REJECTED';

        // case_progress from API = last completed step (1-6)
        const completedStep = this.caseData.caseProgress || 0;
        // Next step after completed is the active (in-progress) one
        const activeStep = completedStep + 1;

        const allSteps: { label: string; step: number }[] = [
            { label: 'Submitted', step: 1 },
            { label: 'Offer Generated', step: 2 },
            { label: isRejected ? 'Offer Rejected' : 'Offer Status', step: 3 },
            { label: 'Patient Payment', step: 4 },
            ...(!this.isAttorney ? [{ label: 'Hospital Payment', step: 5 }] : []),
            { label: 'Release of Lien', step: 6 },
            { label: 'Pending Trust Funds', step: 7 }
        ];

        // If rejected, only show steps up to "Offer Rejected"
        const visibleSteps = isRejected ? allSteps.slice(0, 3) : allSteps;

        this.progressSteps = visibleSteps.map((s) => {
            let status: ProgressStep['status'];

            if (s.step <= completedStep) {
                status = 'completed';
            } else if (s.step === activeStep) {
                status = 'active';
            } else {
                status = 'pending';
            }

            if (s.step === 3 && isRejected) {
                status = 'rejected';
            }

            return { label: s.label, status };
        });
    }

    getDocumentUrl(doc: CaseDocument): string {
        if (!this.caseData?.defaultUrl || !doc.file_path) return '';
        return `${this.caseData.defaultUrl}/${doc.file_path}`;
    }

    viewDocument(doc: CaseDocument) {
        const url = this.getDocumentUrl(doc);
        if (url) {
            window.open(url, '_blank');
        }
    }

    maskValue(value: string): string {
        if (!value || value.length <= 4) return '••••';
        return value.slice(0, 4) + '••••••';
    }

    copyToClipboard(value: string, label: string) {
        navigator.clipboard.writeText(value).then(() => {
            this.snackbar.show(`${label} copied`, 'success');
        });
    }

    offerAmountMatchValidator(group: FormGroup): { [key: string]: boolean } | null {
        const amount = group.get('offer_amount')?.value;
        const confirm = group.get('confirm_offer_amount')?.value;
        if (amount && confirm && amount !== confirm) {
            return { amountMismatch: true };
        }
        return null;
    }

    openOfferModal() {
        this.showOfferModal = true;
    }

    closeOfferModal() {
        this.showOfferModal = false;
        this.offerForm.reset();
    }

    openViewOfferModal() {
        if (!this.inquiryId) return;

        if (this.offerData) {
            this.showViewOfferModal = true;
            return;
        }

        this.caseService.getOfferByCaseId(this.inquiryId).subscribe({
            next: (response) => {
                this.mapOfferResponse(response);
                if (response?.data) {
                    this.showViewOfferModal = true;
                }
            },
            error: () => {
                this.snackbar.show('Error loading offer details', 'error');
            }
        });
    }

    closeViewOfferModal() {
        if (this.progressPercent !== null) return;
        this.showViewOfferModal = false;
    }

    offerActionLoading = false;

    acceptOffer() {
        if (!this.offerId || this.offerActionLoading) return;
        this.offerActionLoading = true;

        this.caseService.acceptOffer(this.offerId).subscribe({
            next: () => {
                this.snackbar.show('Offer accepted successfully', 'success');
                this.closeViewOfferModal();
                this.loadCaseData();
                this.offerActionLoading = false;
            },
            error: () => {
                this.snackbar.show('Error accepting offer', 'error');
                this.offerActionLoading = false;
            }
        });
    }

    rejectOffer() {
        if (!this.offerId || this.offerActionLoading) return;
        this.offerActionLoading = true;

        this.caseService.rejectOffer(this.offerId).subscribe({
            next: () => {
                this.snackbar.show('Offer rejected successfully', 'success');
                this.closeViewOfferModal();
                this.loadCaseData();
                this.offerActionLoading = false;
            },
            error: () => {
                this.snackbar.show('Error rejecting offer', 'error');
                this.offerActionLoading = false;
            }
        });
    }

    /** Called when attorney clicks Sign on a specific document in the View Offer modal.
     *  Closes the offer modal, pre-selects the document tab, and opens the Document Preview
     *  modal directly in the e-sign canvas view (skipping the mode-selection screen). */
    onSignDocument(docIndex: number): void {
        this.docSignIndex       = docIndex;
        this.docStartMode       = 'esign';
        this.docRejectedIndices = [];
        this.showViewOfferModal = false;
        // Small delay so the offer modal closes before the sign modal opens
        setTimeout(() => { this.showDocumentsModal = true; }, 150);
    }

    /** Called when the Documents modal closes — resets startMode back to 'select'
     *  so that next time it's opened via a reminder banner it shows the mode-selection screen.
     *  Also refreshes offer data so reminders reflect the latest document signed status. */
    onDocumentsModalClose(): void {
        this.showDocumentsModal  = false;
        this.docStartMode        = 'select';
        this.docRejectedIndices  = [];
        // Reload offer data silently so hasUnsignedDocuments / allDocumentsSigned update
        this.loadOfferData(true);
    }

    /** Returns the server URL for an already-uploaded cover letter PDF, or null. */
    private getCoverLetterSignedUrl(): string | null {
        if (!this.offerData?.documents || !this.offerData.default_url) return null;
        const doc = this.offerData.documents.find(
            (d: any) => d.document_type === 'Cover Letter' && !!d.file_name_signed
        );
        if (!doc) return null;
        return `${this.offerData.default_url}/${doc.file_path}/${doc.file_name_signed}`;
    }

    /** Uploads a generated cover letter PDF to the server (fire-and-forget). */
    private uploadCoverLetterPdf(pdfBlob: Blob): void {
        if (!this.caseId) return;
        const file = new File([pdfBlob], 'cover-letter.pdf', { type: 'application/pdf' });
        this.caseService.uploadSignedDocuments(this.caseId, [{ id: 1, file }]).subscribe({
            next: () => this.loadOfferData(true),
            error: () => { }
        });
    }

    /** Called when attorney/admin clicks "Download" on the Cover Letter in the offer modal. */
    onDownloadCoverLetter(): void {
        if (!this.inquiryId || this.coverLetterDownloading) return;
        this.coverLetterDownloading = true;
        this.showViewOfferModal     = false;

        // If cover letter is already stored on the server, download directly
        const storedUrl = this.getCoverLetterSignedUrl();
        if (storedUrl) {
            const a = document.createElement('a');
            a.href = storedUrl;
            a.download = 'cover-letter.pdf';
            a.target = '_blank';
            a.click();
            this.coverLetterDownloading = false;
            return;
        }

        // Not stored yet — generate client-side
        this.setProgress(10, 'Loading cover letter…');
        this.documentService.getCoverLetterCaseData(this.inquiryId).subscribe({
            next: async (data) => {
                if (data) {
                    if (data.offer_amount == null && this.offerData?.offer_amount != null) {
                        data.offer_amount = this.offerData.offer_amount;
                    }
                    if (!data.date_of_service && this.caseDateOfService) {
                        data.date_of_service = this.caseDateOfService;
                    }
                }
                this.setProgress(30, 'Rendering document…');
                this.coverLetterData       = data;
                this.showCoverLetterRender = true;
                this.cdr.detectChanges();
                await new Promise(r => setTimeout(r, 80));
                this.setProgress(60, 'Generating PDF…');

                const pdfBlob = await this.generateCoverLetterPdfBlob();
                if (pdfBlob) {
                    const url = URL.createObjectURL(pdfBlob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'cover-letter.pdf';
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 10_000);

                    // Upload to server so next click uses stored PDF
                    this.uploadCoverLetterPdf(pdfBlob);
                }

                this.setProgress(100, 'Complete!');
                await new Promise(r => setTimeout(r, 700));
                this.showCoverLetterRender  = false;
                this.coverLetterData        = null;
                this.coverLetterDownloading = false;
                this.showViewOfferModal     = false;
                this.resetProgress();
            },
            error: () => {
                this.coverLetterDownloading = false;
                this.resetProgress();
                this.snackbar.show('Failed to load cover letter data', 'error');
            }
        });
    }

    // ── Document PDF Download (Ack / ATF / HIPAA) ─────────────────────────
    onDownloadDocPdf(docIndex: 0 | 1 | 2): void {
        if (!this.inquiryId || this.downloadingDocIndex !== null) return;
        this.docPdfIndex = docIndex;
        this.docPdfData  = null;
        this.downloadingDocIndex = docIndex;
        this.showViewOfferModal  = false;
        this.setProgress(10, 'Loading document…');

        const frontendIndex = docIndex as 0 | 1 | 2;
        this.documentService.getCaseDataByType(this.inquiryId, frontendIndex).subscribe({
            next: async (data) => {
                this.setProgress(30, 'Rendering document…');
                this.docPdfData      = data;
                this.showDocPdfRender = true;
                this.cdr.detectChanges();
                await new Promise(r => setTimeout(r, 80));
                this.setProgress(60, 'Generating PDF…');
                await this.generateDocumentPdf(docIndex);
                this.setProgress(100, 'Complete!');
                await new Promise(r => setTimeout(r, 700));
                this.showDocPdfRender    = false;
                this.downloadingDocIndex = null;
                this.showViewOfferModal  = false;
                this.resetProgress();
            },
            error: () => {
                this.downloadingDocIndex = null;
                this.resetProgress();
                this.snackbar.show('Failed to load document data', 'error');
            }
        });
    }

    private readonly docNames: Record<number, string> = {
        0: 'acknowledgment-of-assignment',
        1: 'assignment-of-trust-funds',
        2: 'hipaa-authorization',
        3: 'release-of-lien',
    };
    private readonly docTitles: Record<number, string> = {
        0: 'Acknowledgment of Assignment',
        1: 'Assignment of Trust Funds',
        2: 'HIPAA Authorization',
        3: 'Release of Lien',
    };

    private async generateDocumentPdfBlob(docIndex: 0 | 1 | 2 | 3): Promise<Blob | null> {
        const compMap: Record<number, DocAcknowledgmentComponent | DocAssignmentTrustFundsComponent | DocHipaaAuthorizationComponent | DocReleasOfLienComponent | undefined> = {
            0: this.ackDownloadComp,
            1: this.atfDownloadComp,
            2: this.hipaaDownloadComp,
            3: this.rolDownloadComp,
        };

        const comp = compMap[docIndex];
        const cardEl = comp?.docCard?.nativeElement;
        if (!cardEl) return null;

        const RENDER_W = 960;
        const PDF_W    = 612;      // US Letter width in points
        const SCALE    = 4;

        const wrapper = document.createElement('div');
        wrapper.style.cssText = [
            'position:absolute', 'top:-99999px', 'left:0',
            'width:' + RENDER_W + 'px', 'opacity:0', 'pointer-events:none', 'overflow:visible',
        ].join(';');
        document.body.appendChild(wrapper);

        try {
            const clone = cardEl.cloneNode(true) as HTMLElement;
            clone.style.cssText += ';width:' + RENDER_W + 'px;min-width:' + RENDER_W + 'px;overflow:visible;';
            wrapper.appendChild(clone);

            // Replace signature canvases with blank underlines
            clone.querySelectorAll<HTMLCanvasElement>('canvas').forEach(canvasEl => {
                const blank = document.createElement('div');
                blank.style.cssText = 'width:100%;height:60px;border-bottom:1px solid #aaa;';
                canvasEl.parentElement?.replaceChild(blank, canvasEl);
            });

            // Replace date picker widgets with blank underlines
            clone.querySelectorAll<HTMLElement>('.inline-date-picker').forEach(picker => {
                const blank = document.createElement('div');
                blank.style.cssText = 'width:100%;height:28px;border-bottom:1px solid #aaa;margin-top:4px;';
                picker.parentElement?.replaceChild(blank, picker);
            });

            const pdfDoc = await PDFDocument.create();
            await this.addSmartPaginatedPages(
                pdfDoc, clone, this.docTitles[docIndex], PDF_W, 792, SCALE);

            const bytes = await pdfDoc.save({ useObjectStreams: false });
            return new Blob([(bytes.buffer as ArrayBuffer).slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)], { type: 'application/pdf' });
        } finally {
            document.body.removeChild(wrapper);
        }
    }

    private async generateDocumentPdf(docIndex: 0 | 1 | 2): Promise<void> {
        const pdfBlob = await this.generateDocumentPdfBlob(docIndex);
        if (!pdfBlob) return;
        const url = URL.createObjectURL(pdfBlob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = `${this.docNames[docIndex]}.pdf`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }

    private setProgress(percent: number, label: string): void {
        this.progressPercent = Math.min(percent, 100);
        this.progressLabel = label;
        this.cdr.detectChanges();
    }

    private resetProgress(): void {
        this.progressPercent = null;
        this.progressLabel = '';
        this.cdr.detectChanges();
    }

    private async generateCoverLetterPdfBlob(): Promise<Blob | null> {
        const cardEl = this.coverLetterDownloadComp?.docCard?.nativeElement;
        if (!cardEl) return null;

        const RENDER_W = 960;
        const PDF_W    = 612;      // US Letter width in points
        const SCALE    = 4;

        const wrapper = document.createElement('div');
        wrapper.style.cssText = [
            'position:absolute', 'top:-99999px', 'left:0',
            'width:' + RENDER_W + 'px', 'opacity:0', 'pointer-events:none', 'overflow:visible',
        ].join(';');
        document.body.appendChild(wrapper);

        try {
            const clone = cardEl.cloneNode(true) as HTMLElement;
            clone.style.cssText += ';width:' + RENDER_W + 'px;min-width:' + RENDER_W + 'px;overflow:visible;';
            wrapper.appendChild(clone);

            const pdfDoc = await PDFDocument.create();
            await this.addSmartPaginatedPages(
                pdfDoc, clone, 'Cover Letter', PDF_W, 792, SCALE);

            const bytes = await pdfDoc.save({ useObjectStreams: false });
            return new Blob([(bytes.buffer as ArrayBuffer).slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)], { type: 'application/pdf' });
        } finally {
            document.body.removeChild(wrapper);
        }
    }

    async downloadCoverLetterPdf(): Promise<void> {
        const pdfBlob = await this.generateCoverLetterPdfBlob();
        if (!pdfBlob) return;
        const url = URL.createObjectURL(pdfBlob);
        const a   = document.createElement('a');
        a.href     = url;
        a.download = 'cover-letter.pdf';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }

    // ── Document PDF View (renders in preview modal) ─────────────────────
    onViewCoverLetter(): void {
        if (!this.inquiryId || this.coverLetterViewing) return;
        this.coverLetterViewing = true;
        this.showViewOfferModal = false;

        // If cover letter is already stored on the server, open directly
        const storedUrl = this.getCoverLetterSignedUrl();
        if (storedUrl) {
            window.open(storedUrl, '_blank');
            this.coverLetterViewing = false;
            return;
        }

        // Not stored yet — generate client-side
        this.setProgress(10, 'Loading cover letter…');
        this.documentService.getCoverLetterCaseData(this.inquiryId).subscribe({
            next: async (data) => {
                if (data) {
                    if (data.offer_amount == null && this.offerData?.offer_amount != null) {
                        data.offer_amount = this.offerData.offer_amount;
                    }
                    if (!data.date_of_service && this.caseDateOfService) {
                        data.date_of_service = this.caseDateOfService;
                    }
                }
                this.setProgress(30, 'Rendering document…');
                this.coverLetterData       = data;
                this.showCoverLetterRender = true;
                this.cdr.detectChanges();
                await new Promise(r => setTimeout(r, 80));
                this.setProgress(60, 'Generating PDF…');
                const pdfBlob = await this.generateCoverLetterPdfBlob();
                this.setProgress(100, 'Complete!');
                await new Promise(r => setTimeout(r, 700));
                this.showCoverLetterRender = false;
                this.coverLetterData       = null;
                this.coverLetterViewing    = false;
                this.showViewOfferModal    = false;
                this.resetProgress();
                if (pdfBlob) {
                    const url = URL.createObjectURL(pdfBlob);
                    window.open(url, '_blank');

                    // Upload to server so next click uses stored PDF
                    this.uploadCoverLetterPdf(pdfBlob);
                }
            },
            error: () => {
                this.coverLetterViewing = false;
                this.resetProgress();
                this.snackbar.show('Failed to load cover letter data', 'error');
            }
        });
    }

    onViewDocPdf(docIndex: 0 | 1 | 2): void {
        if (!this.inquiryId || this.viewingDocIndex !== null) return;
        this.docPdfIndex = docIndex;
        this.docPdfData  = null;
        this.viewingDocIndex    = docIndex;
        this.showViewOfferModal = false;
        this.setProgress(10, 'Loading document…');

        this.documentService.getCaseDataByType(this.inquiryId, docIndex).subscribe({
            next: async (data) => {
                this.setProgress(30, 'Rendering document…');
                this.docPdfData      = data;
                this.showDocPdfRender = true;
                this.cdr.detectChanges();
                await new Promise(r => setTimeout(r, 80));
                this.setProgress(60, 'Generating PDF…');
                const pdfBlob = await this.generateDocumentPdfBlob(docIndex);
                this.setProgress(100, 'Complete!');
                await new Promise(r => setTimeout(r, 700));
                this.showDocPdfRender   = false;
                this.viewingDocIndex    = null;
                this.showViewOfferModal = false;
                this.resetProgress();
                if (pdfBlob) {
                    const url = URL.createObjectURL(pdfBlob);
                    window.open(url, '_blank');
                }
            },
            error: () => {
                this.viewingDocIndex = null;
                this.resetProgress();
                this.snackbar.show('Failed to load document data', 'error');
            }
        });
    }

    /**
     * Smart paginator with per-page header/footer.
     *
     * • Hides the document-level header/footer from the clone.
     * • Renders the branded header once into a reusable canvas.
     * • Measures each body-child's bounding rect to find safe break points
     *   (no element is ever sliced mid-content).
     * • Composites [header | content slice | footer bar] for every page.
     * • Footer shows "E.A.S.Y. Liens, LLC — Confidential" (left) and
     *   "{docTitle} — Page X of N" (right) in a navy bar.
     */
    private async addSmartPaginatedPages(
        pdfDoc:    PDFDocument,
        clone:     HTMLElement,
        docTitle:  string,
        PDF_W      = 612,      // US Letter width
        PDF_PAGE_H = 792,      // US Letter height
        SCALE      = 4
    ): Promise<void> {
        const RENDER_W = 960;
        const canvasW  = RENDER_W * SCALE;          // canvas pixel width
        const pxToPt   = PDF_W / canvasW;           // canvas-px → PDF-pt

        // ── 1. Identify structural elements ──────────────────────────────────
        const headerEl  = clone.querySelector<HTMLElement>(
            '.ack-header, .atf-header, .hip-header, .cl-header, .rol-header');
        const goldBarEl = clone.querySelector<HTMLElement>(
            '.ack-gold-bar, .atf-gold-bar, .hip-gold-bar, .cl-gold-bar, .rol-gold-bar');
        const footerEl  = clone.querySelector<HTMLElement>(
            '.ack-footer, .atf-footer, .hip-footer, .cl-footer, .rol-footer');
        const bodyEl    = clone.querySelector<HTMLElement>(
            '.ack-body, .atf-body, .hip-body, .cl-body, .rol-body');
        const styleEl   = clone.querySelector<HTMLStyleElement>('style');

        // ── 2. Render the branded header once ────────────────────────────────
        const headerTemp = document.createElement('div');
        headerTemp.style.cssText =
            `position:absolute;top:-300000px;left:0;width:${RENDER_W}px;overflow:visible;`;
        if (styleEl)   headerTemp.appendChild(styleEl.cloneNode(true));
        if (headerEl)  headerTemp.appendChild(headerEl.cloneNode(true));
        if (goldBarEl) headerTemp.appendChild(goldBarEl.cloneNode(true));
        document.body.appendChild(headerTemp);

        const headerCanvas = await html2canvas(headerTemp, {
            scale: SCALE, useCORS: true, allowTaint: true, logging: false,
            backgroundColor: '#1a2c5b',
            width: RENDER_W, height: headerTemp.scrollHeight,
            scrollX: 0, scrollY: 0, windowWidth: RENDER_W,
        });
        document.body.removeChild(headerTemp);
        const headerPx = headerCanvas.height; // canvas pixels

        // ── 3. Hide doc-level header / footer in the clone ───────────────────
        if (headerEl)  headerEl.style.display  = 'none';
        if (goldBarEl) goldBarEl.style.display = 'none';
        if (footerEl)  footerEl.style.display  = 'none';

        // footer bar: 30 PDF-pts tall  →  canvas pixels
        const footerPtH  = 30;
        const footerPx   = Math.round(footerPtH / pxToPt);

        // gap between header and content: 20 PDF-pts  →  canvas pixels
        const gapPtH = 20;
        const gapPx  = Math.round(gapPtH / pxToPt);

        // safe area above footer: 20 PDF-pts  →  canvas pixels
        const bottomGapPx = Math.round(20 / pxToPt);

        // canvas pixels available for content per page
        const pageCanvasPx = Math.round(PDF_PAGE_H / pxToPt);
        const contentPx    = pageCanvasPx - headerPx - gapPx - footerPx - bottomGapPx;

        // ── 4. Measure child-element tops for safe break candidates ───────────
        await new Promise(r => setTimeout(r, 30)); // let browser reflow

        const cloneRect      = clone.getBoundingClientRect();
        const breakCandidates: number[] = [0];
        if (bodyEl) {
            const collectBreaks = (parent: Element, depth: number) => {
                Array.from(parent.children).forEach(el => {
                    const htmlEl = el as HTMLElement;
                    const r = htmlEl.getBoundingClientRect();
                    const top = (r.top - cloneRect.top) * SCALE;
                    if (top > 0) breakCandidates.push(top);
                    // Go one level deeper for large elements
                    if (depth < 2 && htmlEl.children.length > 0) {
                        collectBreaks(htmlEl, depth + 1);
                    }
                });
            };
            collectBreaks(bodyEl, 0);
            // Sort and deduplicate
            breakCandidates.sort((a, b) => a - b);
            const unique = [breakCandidates[0]];
            for (let i = 1; i < breakCandidates.length; i++) {
                if (breakCandidates[i] - unique[unique.length - 1] > 10) {
                    unique.push(breakCandidates[i]);
                }
            }
            breakCandidates.length = 0;
            breakCandidates.push(...unique);
        }

        // ── 5. Render the full content canvas (header/footer already hidden) ──
        await new Promise(r => setTimeout(r, 20));
        const fullCanvas = await html2canvas(clone, {
            scale: SCALE, useCORS: true, allowTaint: true, logging: false,
            backgroundColor: '#ffffff', width: RENDER_W, height: clone.scrollHeight,
            scrollX: 0, scrollY: 0, windowWidth: RENDER_W,
        });

        // ── 6. Determine page slice boundaries (smart break) ─────────────────
        const slices: Array<{ start: number; end: number }> = [];
        let pageStart = 0;
        for (let c = 1; c < breakCandidates.length; c++) {
            if (breakCandidates[c] - pageStart > contentPx) {
                // Element c would overflow — break at previous candidate
                const breakAt = breakCandidates[c - 1];
                if (breakAt > pageStart) {
                    slices.push({ start: pageStart, end: breakAt });
                    pageStart = breakAt;
                    // Re-check current element against new pageStart
                    if (breakCandidates[c] - pageStart > contentPx) {
                        // Single element taller than a page — force split
                        slices.push({ start: pageStart, end: pageStart + contentPx });
                        pageStart = pageStart + contentPx;
                    }
                } else {
                    // No valid break point — force split at contentPx
                    slices.push({ start: pageStart, end: pageStart + contentPx });
                    pageStart = pageStart + contentPx;
                }
            }
        }
        // Add remaining content
        if (pageStart < fullCanvas.height) {
            slices.push({ start: pageStart, end: fullCanvas.height });
        }
        const totalPages = slices.length;

        // ── 7. Composite each page: header + content slice + footer ───────────
        for (let i = 0; i < totalPages; i++) {
            const { start, end } = slices[i];
            const sliceH   = Math.min(Math.max(end - start, 1), contentPx);

            // Page canvas at full page size
            const pg   = document.createElement('canvas');
            pg.width   = canvasW;
            pg.height  = pageCanvasPx;
            const ctx  = pg.getContext('2d')!;

            // White background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, pg.width, pg.height);

            // Branded header (stretch to full canvas width)
            ctx.drawImage(headerCanvas,
                0, 0, headerCanvas.width, headerCanvas.height,
                0, 0, pg.width, headerPx);

            // Content slice (offset by gap below header)
            ctx.drawImage(fullCanvas,
                0, start, fullCanvas.width, sliceH,
                0, headerPx + gapPx, pg.width, sliceH);

            // Navy footer bar — always at the bottom of the page
            const footerY = pageCanvasPx - footerPx;
            ctx.fillStyle = '#1a2c5b';
            ctx.fillRect(0, footerY, pg.width, footerPx);

            // Footer text
            const fs   = Math.round(8 * SCALE);
            const textY = footerY + Math.round(footerPx * 0.62);
            const mX    = Math.round(32 * SCALE);
            ctx.font      = `${fs}px Helvetica, Arial, sans-serif`;
            ctx.fillStyle = '#9ab0d8';
            ctx.textAlign = 'left';
            ctx.fillText('E.A.S.Y. Liens, LLC \u2014 Confidential', mX, textY);
            ctx.textAlign = 'right';
            ctx.fillText(`${docTitle} \u2014 Page ${i + 1} of ${totalPages}`,
                pg.width - mX, textY);

            // Add page to PDF — all pages are exactly A4 size
            const png = await pdfDoc.embedPng(
                this.dataUrlToUint8Array(pg.toDataURL('image/png')));
            pdfDoc.addPage([PDF_W, PDF_PAGE_H]).drawImage(png, {
                x: 0, y: 0, width: PDF_W, height: PDF_PAGE_H,
            });
        }
    }

    private dataUrlToUint8Array(dataUrl: string): Uint8Array {
        const base64 = dataUrl.split(',')[1];
        const binary = atob(base64);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return bytes;
    }

    openPaymentModal() {
        this.showPaymentModal = true;
    }

    openPaymentDetailsModal() {
        this.showPaymentDetailsModal = true;
    }

    closePaymentDetailsModal() {
        this.showPaymentDetailsModal = false;
    }

    openReceivedPaymentModal() {
        this.showReceivedPaymentModal = true;
    }

    closeReceivedPaymentModal() {
        this.showReceivedPaymentModal = false;
    }

    openReleaseOfLienModal() {
        this.releaseOfLienForm.reset();
        this.showReleaseOfLienModal = true;
    }

    openHospitalPaymentModal() {
        this.hospitalPaymentForm.reset();
        this.showHospitalPaymentModal = true;
    }

    openViewHospitalPaymentModal() {
        this.showViewHospitalPaymentModal = true;
    }

    closeHospitalPaymentModal() {
        this.showHospitalPaymentModal = false;
        this.hospitalPaymentForm.reset();
    }

    submitHospitalPayment() {
        if (this.hospitalPaymentForm.invalid || !this.inquiryId || this.hospitalPaymentSubmitting) return;
        this.hospitalPaymentSubmitting = true;

        const v = this.hospitalPaymentForm.value;
        const formData = new FormData();
        formData.append('case_id', this.inquiryId.toString());
        formData.append('hospital_name', v.hospital_name);
        formData.append('address', v.address);
        formData.append('phone_number', v.phone_number);
        formData.append('street', v.street);
        formData.append('city', v.city);
        formData.append('state', v.state);
        formData.append('zipcode', v.zipcode);
        formData.append('status', 'PENDING');
        formData.append('amount', v.amount);
        if (v.attachement) {
            formData.append('attachement', v.attachement);
            formData.append('admin_attachment', v.attachement);
        }

        this.caseService.submitHospitalPayment(formData).subscribe({
            next: () => {
                this.snackbar.show('Provider Payment Details Entered Successfully', 'success');
                this.hospitalPaymentSubmitting = false;
                this.showHospitalPaymentModal = false;
                this.hospitalPaymentForm.reset();
                this.loadCaseData();
            },
            error: () => {
                this.snackbar.show('Failed to submit hospital payment', 'error');
                this.hospitalPaymentSubmitting = false;
            }
        });
    }

    openUpdateHospitalPaymentModal() {
        this.updateHospitalPaymentForm.reset();
        this.showUpdateHospitalPaymentModal = true;
    }

    closeUpdateHospitalPaymentModal() {
        this.showUpdateHospitalPaymentModal = false;
        this.updateHospitalPaymentForm.reset();
    }

    submitUpdateHospitalPayment() {
        if (this.updateHospitalPaymentForm.invalid || !this.inquiryId || this.updateHospitalPaymentSubmitting) return;
        this.updateHospitalPaymentSubmitting = true;

        const v = this.updateHospitalPaymentForm.value;
        const formData = new FormData();
        formData.append('payment_id', this.hospitalPaymentData?.id?.toString());
        formData.append('cheque_id', v.transaction_id);
        formData.append('cheque_date', v.transaction_date);
        formData.append('amount_paid', v.amount);
        if (v.receipt) {
            formData.append('admin_attachment', v.receipt);
        }

        this.caseService.updateHospitalPaymentStatus(formData).subscribe({
            next: () => {
                this.snackbar.show('Hospital payment status updated successfully', 'success');
                this.updateHospitalPaymentSubmitting = false;
                this.showUpdateHospitalPaymentModal = false;
                this.updateHospitalPaymentForm.reset();
                this.loadCaseData();
            },
            error: () => {
                this.snackbar.show('Failed to update hospital payment status', 'error');
                this.updateHospitalPaymentSubmitting = false;
            }
        });
    }

    async generateReleaseOfLien(): Promise<void> {
        if (!this.caseData || this.generatingRol) return;
        this.generatingRol = true;
        this.setProgress(10, 'Preparing document…');

        const nameParts = (this.caseData.clientName || '').split(' ');
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';

        this.rolPdfData = {
            case_id: this.inquiryId,
            patient_first_name: firstName,
            patient_last_name: lastName,
            patient_dob: this.caseData.clientDob || null,
            patient_address: null,
            provider_name: this.caseData.hospitalName || null,
            account_number: this.caseData.hospitalAccountNumber || null,
            date_of_service: this.caseDateOfService || null,
            full_billed_charges: this.caseData.lienBalance || null,
            trust_amount: this.caseData.reductionAmount ?? this.caseData.lienBalance ?? null,
            offer_amount: this.offerData?.offer_amount ?? null,
            attorney_name: this.caseData.attorneyName || null,
            attorney_firm: this.caseData.attorneyFirm || null,
            ssn_last4: null
        };

        this.showRolPdfRender = true;
        this.cdr.detectChanges();
        await new Promise(r => setTimeout(r, 80));
        this.setProgress(60, 'Generating PDF…');

        const pdfBlob = await this.generateDocumentPdfBlob(3);
        if (pdfBlob) {
            this.setProgress(100, 'Complete!');
            const url = URL.createObjectURL(pdfBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'release-of-lien.pdf';
            a.click();
            URL.revokeObjectURL(url);
            setTimeout(() => {
                this.showRolPdfRender = false;
                this.rolPdfData = null;
                this.generatingRol = false;
                this.resetProgress();
            }, 700);
        } else {
            this.showRolPdfRender = false;
            this.rolPdfData = null;
            this.generatingRol = false;
            this.resetProgress();
            this.snackbar.show('Failed to generate PDF', 'error');
        }
    }

    closeReleaseOfLienModal() {
        this.showReleaseOfLienModal = false;
        this.releaseOfLienForm.reset();
        this.releaseOfLienSubmitting = false;
    }

    openReceivedMoneyConfirmModal() {
        this.receivedMoneyForm.reset();
        this.showReceivedMoneyConfirmModal = true;
    }

    closeReceivedMoneyConfirmModal() {
        this.showReceivedMoneyConfirmModal = false;
        this.receivedMoneyForm.reset();
        this.receivedMoneySubmitting = false;
    }

    confirmReceivedMoney() {
        if (!this.inquiryId || this.receivedMoneyForm.invalid || this.receivedMoneySubmitting) return;
        this.receivedMoneySubmitting = true;

        const formVal = this.receivedMoneyForm.value;
        const formData = new FormData();
        formData.append('case_id', this.inquiryId.toString());
        formData.append('amount', formVal.amount.toString());
        formData.append('cheque_id', formVal.cheque_id);
        formData.append('cheque_date', formVal.cheque_date);
        if (formVal.attachement) {
            formData.append('attachement', formVal.attachement);
        }

        this.caseService.receivedMoney(formData).subscribe({
            next: () => {
                this.closeReceivedMoneyConfirmModal();
                this.showCelebration = true;
                setTimeout(() => {
                    this.showCelebration = false;
                    this.loadCaseData();
                }, 5000);
            },
            error: () => {
                this.snackbar.show('Error updating case status', 'error');
                this.receivedMoneySubmitting = false;
            }
        });
    }

    submitReleaseOfLien() {
        if (this.releaseOfLienForm.invalid || !this.inquiryId || this.releaseOfLienSubmitting) return;
        this.releaseOfLienSubmitting = true;
        const file: File = this.releaseOfLienForm.value.document;

        this.caseService.uploadReleaseOfLien(this.inquiryId, file).subscribe({
            next: () => {
                this.snackbar.show('Release of Lien uploaded successfully', 'success');
                this.closeReleaseOfLienModal();
                this.loadCaseData();
            },
            error: () => {
                this.snackbar.show('Error uploading Release of Lien', 'error');
                this.releaseOfLienSubmitting = false;
            }
        });
    }

    closePaymentModal() {
        this.showPaymentModal = false;
        this.paymentForm.reset();
    }

    submitPayment() {
        if (this.paymentForm.valid && this.caseId && this.offerId) {
            this.paymentSubmitting = true;

            const formVal = this.paymentForm.value;
            const payload = {
                case_id: this.caseId,
                case_offer_id: this.offerId,
                full_name: formVal.full_name,
                address: `${formVal.mailing_street}, ${formVal.mailing_city}, ${formVal.mailing_state} ${formVal.mailing_zip}`,
                phone_number: formVal.phone_number,
                email: formVal.email_address || '',
                street: formVal.mailing_street,
                city: formVal.mailing_city,
                state: formVal.mailing_state,
                zipcode: formVal.mailing_zip,
                business_or_residence: formVal.address_type === 'BUSINESS' ? 1 : 2
            };

            this.caseService.submitPaymentDetails(payload).subscribe({
                next: () => {
                    this.snackbar.show('Payment details submitted successfully', 'success');
                    this.closePaymentModal();
                    this.loadCaseData();
                    this.loadPaymentDetails();
                    this.paymentSubmitting = false;
                },
                error: () => {
                    this.snackbar.show('Error submitting payment details', 'error');
                    this.paymentSubmitting = false;
                }
            });
        }
    }

    openSendPaymentModal() {
        this.sendPaymentForm.reset();
        this.showSendPaymentModal = true;
    }

    closeSendPaymentModal() {
        this.showSendPaymentModal = false;
        this.sendPaymentForm.reset();
        this.sendPaymentSubmitting = false;
    }

    submitSendPayment() {
        if (this.sendPaymentForm.invalid || !this.inquiryId || this.sendPaymentSubmitting) return;
        this.sendPaymentSubmitting = true;

        const formVal = this.sendPaymentForm.value;
        const formData = new FormData();
        formData.append('case_id', this.inquiryId.toString());
        formData.append('case_offer_id', (this.offerId || '').toString());
        formData.append('status', 'SENT');
        formData.append('transaction_id', formVal.transaction_id);
        formData.append('transaction_date', formVal.transaction_date);
        formData.append('amount', formVal.amount.toString());
        if (formVal.receipt) {
            formData.append('receipt', formVal.receipt);
        }

        this.caseService.updatePaymentStatus(formData).subscribe({
            next: () => {
                this.snackbar.show('Payment sent successfully', 'success');
                this.closeSendPaymentModal();
                this.loadCaseData();
            },
            error: () => {
                this.snackbar.show('Error sending payment', 'error');
                this.sendPaymentSubmitting = false;
            }
        });
    }

    submitOffer() {
        if (this.offerForm.valid && this.caseId) {
            const { confirm_offer_amount, ...formValues } = this.offerForm.value;
            const payload = {
                case_submission_id: this.inquiryId,
                ...formValues
            };

            this.caseService.createOffer(payload).subscribe({
                next: () => {
                    this.snackbar.show('Offer generated successfully', 'success');
                    this.closeOfferModal();
                    this.loadCaseData();
                },
                error: () => {
                    this.snackbar.show('Error generating offer', 'error');
                }
            });
        }
    }
}
